
import { PageLayout } from "@/components/layout/PageLayout";
import { HeroSection } from "@/components/specializations/microsoft365security/HeroSection";
import { ServiceCards } from "@/components/specializations/microsoft365security/ServiceCards";
import { ProcessSteps } from "@/components/specializations/microsoft365security/ProcessSteps";
import { CaseStudiesSection } from "@/components/services/microsoft365security/CaseStudiesSection";
import { ValueMetricsSection } from "@/components/specializations/microsoft365security/ValueMetricsSection";
import { HiddenCapabilitiesSection } from "@/components/specializations/microsoft365security/HiddenCapabilitiesSection";
import { GuideSection } from "@/components/specializations/microsoft365security/GuideSection";
import { CtaSection } from "@/components/specializations/microsoft365security/CtaSection";
import { PlatformValueSection } from "@/components/specializations/microsoft365security/PlatformValueSection";
import { FAQSection } from "@/components/services/microsoft365security/FAQSection";
import { ServiceSchema } from "@/components/seo/ServiceSchema";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { OptimizedImage } from "@/components/ui/optimized-image";

const Microsoft365Security = () => {
  const pageTitle = "Microsoft 365 Security Experts | Secure Your Cloud | edgefi";
  const pageDescription = "Transform your Microsoft 365 environment from a vulnerability into your strongest security asset. Expert configuration, monitoring & incident response for M365.";
  const pageUrl = window.location.origin + "/services/microsoft-365-security";

  // FAQ data to be used for SEO and the FAQ section
  const faqData = [
    {
      question: "What is Microsoft 365 security?",
      answer: "Microsoft 365 security is a comprehensive set of security features and capabilities built into Microsoft 365 that protect your organization's data, identities, applications, and devices. edgefi specializes in optimizing these capabilities to provide robust protection against modern threats."
    },
    {
      question: "How can edgefi improve my Microsoft 365 security posture?",
      answer: "edgefi improves your Microsoft 365 security posture by conducting in-depth assessments, implementing security best practices, configuring advanced security features, providing ongoing monitoring, and delivering quick incident response. Our experts maximize your existing M365 licenses to ensure you're getting the full security value from your investment."
    },
    {
      question: "How much does Microsoft 365 security optimization cost?",
      answer: "Microsoft 365 security optimization costs vary based on your organization's size, complexity, and specific security needs. In many cases, we can help you save money by optimizing your existing licenses rather than purchasing additional security tools. Contact us for a personalized consultation and quote."
    },
    {
      question: "Can I improve Microsoft 365 security without additional licenses?",
      answer: "Yes, most organizations can significantly improve their Microsoft 365 security without purchasing additional licenses. edgefi specializes in helping businesses maximize the security capabilities already included in their existing Microsoft 365 subscriptions, many of which go unused or improperly configured."
    },
    {
      question: "How long does it take to implement Microsoft 365 security improvements?",
      answer: "The timeline for implementing Microsoft 365 security improvements typically ranges from 2-6 weeks depending on your organization's size and complexity. Our process includes assessment, planning, implementation, testing, and knowledge transfer. We prioritize high-impact, low-disruption changes that can be quickly deployed to improve your security posture."
    }
  ];

  return (
    <PageLayout
      title={pageTitle}
      description={pageDescription}
      canonicalUrl={pageUrl}
      keywords={["Microsoft 365 security", "M365 secure score", "Microsoft Defender", "cloud security", "data protection", "identity security", "Microsoft security", "cloud protection"]}
      noIndex={false} // Ensure this page is indexed
    >
      {/* SEO Schema Components */}
      <ServiceSchema 
        name="Microsoft 365 Security Services"
        description="Expert configuration, monitoring, and optimization of Microsoft 365 security settings and capabilities to protect your organization's data, identities, and devices."
        url={pageUrl}
        serviceType="Cybersecurity Service"
        areaServed="United States"
        image="/lovable-uploads/3faf62f0-dd43-43cc-82c2-9329f89d269f.png"
        offers={{
          description: "Microsoft 365 security assessment, implementation, and ongoing management",
          availability: "https://schema.org/InStock"
        }}
      />

      <BreadcrumbSchema 
        items={[
          { name: "Home", url: "/", position: 1 },
          { name: "Services", url: "/services", position: 2 },
          { name: "Microsoft 365 Security", url: pageUrl, position: 3 }
        ]}
      />

      <LocalBusinessSchema
        name="edgefi"
        description="Cybersecurity services designed to evolve with the times"
        url="https://edgefi.com"
        logo="/lovable-uploads/3faf62f0-dd43-43cc-82c2-9329f89d269f.png"
        email="contact@edgefi.com"
        telephone="+15033509138"
        priceRange="$$$"
        address={{
          streetAddress: "811 E Burnside St",
          addressLocality: "Portland",
          addressRegion: "OR",
          postalCode: "97214",
          addressCountry: "US"
        }}
        areaServed="United States"
      />

      {/* Hero Section */}
      <HeroSection />

      {/* Service Cards */}
      <ServiceCards />

      {/* Platform Value Section */}
      <PlatformValueSection />

      {/* Process Steps */}
      <ProcessSteps />

      {/* Hidden Capabilities Section */}
      <HiddenCapabilitiesSection />

      {/* Value Metrics Section */}
      <ValueMetricsSection />

      {/* Case Studies Section */}
      <CaseStudiesSection />

      {/* FAQ Section - New addition for SEO and UX */}
      <FAQSection faqItems={faqData} />

      {/* Guide Section */}
      <GuideSection />

      {/* CTA Section */}
      <CtaSection />
    </PageLayout>
  );
};

export default Microsoft365Security;
